/**
 * @typedef {ReturnType<typeof import('widgets/forms/AutocompleteField').default>} AutocompleteField
 */

import {
    initGoogleMapsApi,
    initGoogleAutocomplete,
    parseAddress
} from 'widgets/toolbox/googleMgr';

/**
 * @param {AutocompleteField} AutocompleteField Base widget for extending
 * @returns {typeof GoogleAutocompleteField} Input Textarea widget
 */
export default function (AutocompleteField) {
    /**
     * @category widgets
     * @subcategory forms
     * @class GoogleAutocompleteField
     * @augments AutocompleteField
     * @classdesc Represents GoogleAutocompleteField components that implement Google Autocomplete functionality using Google Places API.
     * It has next features:
     * 1. Inits google autocomplete once user is clicked on the input field
     * 2. Emits widget event with the suggested address by google autocomplete API
     *
     * GoogleAutocompleteField widget should receive countryCode
     * and google maps API key values in order to properly init the google autocomplete functionality
     * @property {string} data-country-code - current country code value
     * @property {string} data-api-key - google maps API key
     * @example <caption>Example of Google autocomplete field </caption>
     *    <div
     *        data-widget="googleAutocompleteField"
     *        data-widget-event-placechanged="handlePlaceChange"
     *        data-id="googleAutocomplete"
     *        data-api-key="..."
     *        data-country-code="US"
     *        ...
     *    >
     *        <label
     *            class="b-form_section-label"
     *            for="dwfrm_shipping_shippingAddress_addressFields_quickFinder"
     *            ...
     *        >
     *            Quick Address Finder
     *        </label>
     *        <input
     *            data-ref="field"
     *            id="dwfrm_shipping_shippingAddress_addressFields_quickFinder"
     *            type="text"
     *            class="b-input pac-target-input m-valid"
     *            placeholder="Start typing address or ZIP code"
     *            aria-describedby="dwfrm_shipping_shippingAddress_addressFields_quickFinder-error"
     *            name="dwfrm_shipping_shippingAddress_addressFields_quickFinder"
     *            value=""
     *            maxlength="2147483647"
     *            data-event-focus="handleFocus"
     *            data-event-blur="validate"
     *            autocomplete="off"
     *            ...
     *        >
     *        <div
     *            role="alert"
     *            class="b-form_section-message m-hide"
     *            data-ref="errorFeedback"
     *            id="dwfrm_shipping_shippingAddress_addressFields_quickFinder-error"
     *            hidden="hidden"
     *            ...
     *        >
     *            ...error text
     *        </div>
     *    </div>
     */
    class GoogleAutocompleteField extends AutocompleteField {
        /**
          * @description Initialize widget logic
          * @returns {void}
          */
        init() {
            super.init();

            this.inited = false;
        }

        /**
         * @listens dom:focus
         * @description Inits google API once field is focused
         * @returns {void}
         */
        handleFocus() {
            if (this.inited) {
                return;
            }

            initGoogleMapsApi(this.config.apiKey).then(() => {
                const field = /** @type {HTMLInputElement} */ (this.ref('field').get());

                if (field) {
                    initGoogleAutocomplete(field, this.prefs().countryCode)
                        .then(this.initEvents.bind(this))
                        .then(() => {
                            this.inited = true;
                        });
                }
            });
        }

        /**
         * @description Inits google API events
         * @param {object} autocomplete - google autocomplete
         * @returns {void}
         */
        initEvents(autocomplete) {
            if (!autocomplete) {
                return;
            }

            this.autocomplete = autocomplete;

            this.autocomplete.addListener('place_changed', () => {
                var place = autocomplete.getPlace();
                var addressComponents = place.address_components;

                if (addressComponents) {
                    parseAddress(addressComponents, this.prefs().countryCode).then((parsedAddress) => {
                        if (parsedAddress) {
                            /**
                             * @description Event to trigger address form fields prefill
                             * @event AddressForm#handlePlaceChange
                             */
                            this.emit('placechanged', parsedAddress);
                        }
                    });
                }
            });
        }

        /**
         * @description Updates google API restrictions
         * @param {string} countryCode - current country code
         * @returns {void}
         */
        updateState(countryCode) {
            if (countryCode && this.autocomplete) {
                this.autocomplete.setComponentRestrictions({
                    country: countryCode
                });
            }
        }
    }

    return GoogleAutocompleteField;
}
